/**
 * The front of the house.
 *
 * Until now a stranger who typed the address in was shown a login box, which
 * answers a question nobody asked: they do not yet know what this is, and a
 * password field is a poor way to tell them. This is the page that tells them,
 * and the login is one click away rather than the first thing in their way.
 *
 * The brand book is specific about what this must not look like — no casino,
 * no promises of money, no cash imagery — and specific about what it should:
 * a tournament brand, cinematic and serious, that treats poker as a contest of
 * decisions. Every claim on the page is one the room can actually keep, and
 * the numbers in the middle of it are read from the tables rather than typed
 * into a template.
 */

import { useEffect, useState } from 'react';

import { CardIcon, Chip } from '../brand.tsx';
import { api } from '../server.ts';
import { Avatar } from './avatar.tsx';

interface Live {
  players: number;
  tables: number;
  tablesOpen: number;
  tournaments: number;
  handsToday: number;
}

interface TopPlayer {
  playerId: string;
  name: string;
  rank: number | null;
  bb100: number;
}

/**
 * The four things the room does that another room does not.
 *
 * Kept to what is actually true here. "Advanced analysis" and "world-class"
 * are what every site says; a shuffle a player can check on their own machine
 * is a thing that either exists or does not, and this one does.
 */
const PROOFS: readonly { title: string; text: string }[] = [
  {
    title: 'تعهد پیش از پخش',
    text: 'هش بذر سرور قبل از اینکه کارتی پخش شود منتشر می‌شود. از آن لحظه سرور دیگر نمی‌تواند دسته را عوض کند بی‌آنکه تعهدش نخواند.',
  },
  {
    title: 'بذر شما هم در آن هست',
    text: 'می‌توانید بذر خودتان را اضافه کنید. سرور پیش از دیدن آن تعهد داده، پس نمی‌تواند دسته‌ای بسازد که به شما بخورد.',
  },
  {
    title: 'بررسی روی دستگاه خودتان',
    text: 'بعد از دست، بذر فاش می‌شود و مرورگر شما دسته را از نو می‌سازد و با آنچه پخش شده مقایسه می‌کند. حرف ما در این حساب نقشی ندارد.',
  },
  {
    title: 'بدون سوگیری پنهان',
    text: 'Fisher-Yates با نمونه‌گیری رد. باقیمانده‌ی ساده بعضی کارت‌ها را کمی بیشتر می‌آورد — آن‌قدر کم که کسی نفهمد و آن‌قدر که یک نقص واقعی باشد.',
  },
];

interface Feature {
  readonly key: string;
  readonly tab: string;
  readonly title: string;
  readonly lead: string;
  readonly points: readonly string[];
}

/**
 * What the room actually does, in the order somebody new cares about it.
 *
 * Every line is a thing that exists. A feature list on a poker site is the
 * easiest place in the world to write a cheque the software cannot cash, and
 * the room would rather be short than untrue.
 */
const FEATURES: readonly Feature[] = [
  {
    key: 'cash',
    tab: 'Cash Games',
    title: 'میزهای کش، از میکرو تا بالا',
    lead: 'نو-لیمیت هولدم شش‌نفره و هدزآپ، با موتور اختصاصی و بدون هیچ کلاینت خریداری‌شده.',
    points: [
      'ساید پات‌ها، دد باتن و آنته — قواعد کامل یک اتاق واقعی، نه نسخه‌ی ساده‌شده.',
      'تایم‌بنک برای تصمیم‌های بزرگ، و ساعت کوتاه برای کسی که سر میز نیست.',
      'نشستن، ترک میز و ری‌بای بدون قطع شدن بازی بقیه.',
      'قطع اتصال دستتان را از بین نمی‌برد — برمی‌گردید سر همان صندلی.',
    ],
  },
  {
    key: 'tournaments',
    tab: 'Tournaments',
    title: 'سیت‌اند‌گو و تورنمنت چندمیزی',
    lead: 'از شش‌نفره‌ی تک‌میزی تا میدان‌های چندمیزی با تعادل خودکار میزها.',
    points: [
      'ساختار بلایند واقعی با آنته از سطح چهار به بعد.',
      'تعادل میزها و بازی دست‌به‌دست روی حباب، همان‌طور که باید.',
      'ساختار جایزه بر اساس اندازه‌ی میدان، و پرداخت خودکار.',
      'رتبه و جایزه‌ی هر تورنمنت در پروفایل شما ثبت می‌شود.',
    ],
  },
  {
    key: 'fair',
    tab: 'Provably Fair',
    title: 'شافل قابل اثبات',
    lead: 'هر دست را می‌توانید خودتان بررسی کنید — روی دستگاه خودتان، بدون اینکه حرف ما در آن نقشی داشته باشد.',
    points: [
      'پیش از پخش، هش بذر سرور منتشر می‌شود؛ سرور دیگر نمی‌تواند نظرش را عوض کند.',
      'می‌توانید بذر خودتان را هم اضافه کنید.',
      'بعد از دست، بذر فاش می‌شود و مرورگر شما دسته‌کارت را از نو می‌سازد و مقایسه می‌کند.',
      'Fisher-Yates با نمونه‌گیری رد — بدون سوگیریِ باقیمانده که هیچ‌کس متوجهش نمی‌شود.',
    ],
  },
  {
    key: 'ranking',
    tab: 'Ranking',
    title: 'رتبه‌بندی و تحلیل بازی',
    lead: 'پوکر بازی تصمیم است، و تصمیم‌ها قابل اندازه‌گیری‌اند.',
    points: [
      'رنکینگ بر اساس BB/100 و ROI — نه بر اساس مبلغ برده.',
      'VPIP، PFR، ضریب تهاجم و WTSD، کنار محدوده‌ای که بازیکنان برنده در آن هستند.',
      'امتیاز بازیکن که تا وقتی دست کافی بازی نکرده‌اید اصلاً عددی نشان نمی‌دهد.',
      'کابینت افتخارات: قهرمانی‌ها، فینال‌تیبل‌ها و بزرگ‌ترین پاتتان.',
    ],
  },
  {
    key: 'history',
    tab: 'Hand History',
    title: 'تاریخچه‌ی کامل دست‌ها',
    lead: 'هر دستی که بازی کرده‌اید، استریت به استریت، با پوزیشن هر بازیکن.',
    points: [
      'چهار ستون پری‌فلاپ تا ریور، با پاتی که در پایان هر کدام بود.',
      'پوزیشن هر حرکت — «باتن رِیز کرد»، نه «صندلی ۴».',
      'کارت‌های خودتان همیشه، و کارت بقیه هرجا شودان شده باشد.',
      'از سر میز و بدون ترک صندلی قابل باز کردن است.',
    ],
  },
];

export function Landing({ onEnter }: { onEnter: () => void }) {
  const [feature, setFeature] = useState(FEATURES[0]!.key);
  const [live, setLive] = useState<Live | null>(null);
  const [top, setTop] = useState<TopPlayer[]>([]);

  useEffect(() => {
    let alive = true;
    const read = async (): Promise<void> => {
      try {
        const response = await fetch(api('/api/live'), { cache: 'no-store' });
        if (response.ok && alive) setLive((await response.json()) as Live);
      } catch {
        // A front page that cannot reach the room should still be a front
        // page. The numbers simply do not appear.
      }
    };
    void read();
    const timer = setInterval(read, 20_000);

    // The board is public, so the front page can show it without asking
    // anybody to sign in — which is the point of a leaderboard.
    void (async () => {
      try {
        const response = await fetch(api('/api/leaderboard?board=cash&window=30d'), {
          cache: 'no-store',
        });
        if (!response.ok || !alive) return;
        const body = (await response.json()) as { rows: TopPlayer[] };
        setTop(body.rows.filter((row) => row.rank !== null).slice(0, 5));
      } catch {
        // The podium simply does not appear.
      }
    })();

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const shown = FEATURES.find((entry) => entry.key === feature) ?? FEATURES[0]!;

  return (
    <div className="landing">
      <header className="site-head">
        <a className="site-mark" href="/" aria-label="Iran Poker Tour">
          <CardIcon className="site-mark-icon" />
          <span className="site-mark-word lat-ui">
            Iran Poker <b>Tour</b>
          </span>
        </a>
        <nav className="site-nav">
          <a href="/leaderboard">Ranking</a>
          <a href="/download">Get the App</a>
        </nav>
        <span className="spacer" />
        <button className="btn-plain small" onClick={onEnter}>
          Sign In
        </button>
        <button className="btn-check small" onClick={onEnter}>
          Play Now
        </button>
      </header>

      {/* The thesis, and the brand's own line. */}
      <section className="hero">
        <div className="hero-copy">
          <div className="hero-eyebrow lat-ui">
            <CardIcon className="hero-eyebrow-mark" />
            Play Poker Online
          </div>
          <h1>
            جایی که قهرمانان
            <br />
            پوکر ایران ساخته می‌شوند
          </h1>
          <p>
            Iran Poker Tour صحنه‌ی رسمی رقابت برای بازیکنان فارسی‌زبان است. پول مجازی،
            بدون خرید چیپ، بدون قمار — رقابتی ذهنی که در آن مهارت، ریاضی و کنترل احساسات
            تصمیم می‌گیرند.
          </p>
          <div className="hero-actions">
            <button className="btn-check" onClick={onEnter}>
              شروع بازی
            </button>
            <a className="btn-plain" href="/leaderboard">
              دیدن رنکینگ
            </a>
          </div>
          <p className="hero-fine lat-ui">Ranked by skill. Remembered by legacy.</p>
        </div>

        {/* Drawn rather than photographed: the brand's own chips and its own
            card, which is also the only imagery the guide allows. */}
        <div className="hero-art" aria-hidden="true">
          <div className="hero-felt" />
          <Chip kind="brand" className="hero-chip c1" />
          <Chip kind="ivory" value={25} className="hero-chip c2" />
          <Chip kind="midnight" value={100} className="hero-chip c3" />
          <Chip kind="red" value={500} className="hero-chip c4" />
          <CardIcon className="hero-card" />
        </div>
      </section>

      {/* Real numbers, or none. */}
      <section className="live-strip">
        <div className="live-label lat-ui">
          Now on <b>IPT</b>
        </div>
        <div className="live-figures">
          <Figure value={live?.players} label="بازیکن سر میز" />
          <Figure value={live?.tables} label="میز در حال بازی" />
          <Figure value={live?.tournaments} label="تورنمنت باز" />
          <Figure value={live?.handsToday} label="دست از آخرین راه‌اندازی" />
        </div>
      </section>

      <section className="features">
        <div className="feature-tabs" role="tablist">
          {FEATURES.map((entry) => (
            <button
              key={entry.key}
              role="tab"
              aria-selected={entry.key === feature}
              className={entry.key === feature ? 'on' : ''}
              onClick={() => setFeature(entry.key)}
            >
              {entry.tab}
            </button>
          ))}
        </div>

        <div className="feature-panel">
          <div className="feature-copy">
            <h2>{shown.title}</h2>
            <p className="feature-lead">{shown.lead}</p>
          </div>
          <ul className="feature-points">
            {shown.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why the cards can be trusted, which is the one claim here that
          another room cannot copy by writing it down. */}
      <section className="proof">
        <div className="section-head">
          <CardIcon className="section-mark" />
          <h2>شافل قابل اثبات</h2>
        </div>
        <p className="section-lead">
          هر اتاق پوکری در تاریخ متهم به تقلب شده، و «به ما اعتماد کنید» جواب ضعیفی است.
          این یکی را می‌توانید خودتان حساب کنید.
        </p>
        <div className="proof-grid">
          {PROOFS.map((proof) => (
            <div key={proof.title} className="proof-item">
              <h3>{proof.title}</h3>
              <p>{proof.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The podium. Real names off the real board, and nothing at all when
          nobody has qualified yet — an empty podium is honest and a fabricated
          one is the first lie a room tells. */}
      {top.length >= 3 && (
        <section className="podium-section">
          <div className="section-head">
            <CardIcon className="section-mark" />
            <h2>Top Players</h2>
          </div>
          <p className="section-lead">بر اساس BB/100 در سی روز گذشته.</p>
          <div className="podium">
            {orderForPodium(top).map((player) => (
              <div
                key={player.playerId}
                className={`podium-slot place${player.rank ?? 0}${player.rank === 1 ? ' first' : ''}`}
              >
                <span className="podium-rank lat-ui">{player.rank}</span>
                <Avatar name={player.name} />
                <div className="podium-name">{player.name}</div>
                <div className="podium-figure num lat-ui">
                  {player.bb100 > 0 ? '+' : ''}
                  {player.bb100.toFixed(1)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="closing">
        <h2>هر قهرمانی از یک فاینال‌تیبل شروع می‌کند</h2>
        <p>
          حساب باز کنید، چیپ روزانه‌تان را بگیرید و سر یک میز بنشینید. رنکینگ از همان
          دست اول شروع به شمردن می‌کند.
        </p>
        <button className="btn-check" onClick={onEnter}>
          ساختن حساب
        </button>
      </section>

      <footer className="site-foot">
        <div className="foot-mark">
          <CardIcon className="foot-icon" />
          <span className="lat-ui">Iran Poker Tour</span>
        </div>
        <nav className="foot-links">
          <div>
            <h4 className="lat-ui">Play</h4>
            <a href="/">میزهای کش</a>
            <a href="/">تورنمنت‌ها</a>
            <a href="/download">نرم‌افزار</a>
          </div>
          <div>
            <h4 className="lat-ui">Compete</h4>
            <a href="/leaderboard">رنکینگ</a>
            <a href="/profile">پروفایل من</a>
            <a href="/history">تاریخچه‌ی دست‌ها</a>
          </div>
        </nav>
        <p className="disclaimer">
          {/* The brand book's own wording, and it is not decoration: it is the
              difference between what this is and what it must never be taken
              for. */}
          تور پوکر ایران، پوکر را به‌عنوان یک رقابت ذهنی مبتنی بر مهارت، استراتژی، نظم و
          مسئولیت‌پذیری معرفی می‌کند. IPT کازینو، سایت شرط‌بندی یا پلتفرم قمار نیست و با
          پول مجازی بازی می‌شود.
        </p>
      </footer>
    </div>
  );
}

/**
 * A live figure, or a dash.
 *
 * A dash while it loads and a dash if the room cannot be reached — never a
 * zero, which would say something specific and false.
 */
/**
 * The winner in the middle, the rest either side.
 *
 * A podium is not a list: second stands to one side of first and third to the
 * other, which is what makes it read as a result at a glance rather than as a
 * table with pictures.
 */
function orderForPodium(players: readonly TopPlayer[]): TopPlayer[] {
  const byRank = [...players].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  const out: TopPlayer[] = [];
  byRank.forEach((player, index) => {
    if (index === 0) out.splice(Math.floor(out.length / 2), 0, player);
    else if (index % 2 === 1) out.unshift(player);
    else out.push(player);
  });
  return out;
}

function Figure({ value, label }: { value: number | undefined; label: string }) {
  return (
    <div className="live-figure">
      <b className="num lat-ui">{value === undefined ? '—' : value.toLocaleString('en-US')}</b>
      <span>{label}</span>
    </div>
  );
}
